/**
 * License Overview Page
 *
 * Shows license usage across all organizations (or just the effective org when assuming)
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useRoleAssumption } from '../../../context/RoleAssumptionContext';
import { getAllOrganizations, recalculateSeatUsage } from '../../../services/firestore';
import type { Organization } from '../../../services/firestore';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import './LicenseOverview.css';

interface LicenseSummary {
  totalAnalystSeats: number;
  usedAnalystSeats: number;
  totalBankSeats: number;
  usedBankSeats: number;
}

export const LicenseOverview: React.FC = () => {
  const { isPlatformAdmin } = useAuth();
  const { effectiveIsPlatformAdmin, effectiveOrganization } = useRoleAssumption();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [summary, setSummary] = useState<LicenseSummary>({
    totalAnalystSeats: 0,
    usedAnalystSeats: 0,
    totalBankSeats: 0,
    usedBankSeats: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recalculating, setRecalculating] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchOrganizations();
  }, [isPlatformAdmin, effectiveIsPlatformAdmin, effectiveOrganization?.id]);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use effective role to determine what to show
      // When Platform Admin assumes CRA Admin, show only that org's licenses
      const shouldShowAllOrgs = isPlatformAdmin && effectiveIsPlatformAdmin;
      let relevantOrgs: Organization[] = [];

      if (shouldShowAllOrgs) {
        // Platform admin viewing as platform admin - show all orgs
        relevantOrgs = await getAllOrganizations();
      } else if (effectiveOrganization) {
        // Assuming a role with an org, or actual org admin - show only that org
        relevantOrgs = [effectiveOrganization];
      }

      setOrganizations(relevantOrgs);

      // Calculate summary
      const newSummary = relevantOrgs.reduce(
        (acc, org) => ({
          totalAnalystSeats: acc.totalAnalystSeats + (org.licenses?.analyst_seats || 0),
          usedAnalystSeats: acc.usedAnalystSeats + (org.licenses?.analyst_seats_used || 0),
          totalBankSeats: acc.totalBankSeats + (org.licenses?.bank_seats || 0),
          usedBankSeats: acc.usedBankSeats + (org.licenses?.bank_seats_used || 0),
        }),
        { totalAnalystSeats: 0, usedAnalystSeats: 0, totalBankSeats: 0, usedBankSeats: 0 }
      );

      setSummary(newSummary);
    } catch (err) {
      console.error('Error fetching organizations:', err);
      setError('Failed to load license data');
    } finally {
      setLoading(false);
    }
  };

  const getUsagePercentage = (used: number, total: number) => {
    if (total === 0) return 0;
    return Math.round((used / total) * 100);
  };

  const getUsageClass = (percentage: number) => {
    if (percentage >= 90) return 'critical';
    if (percentage >= 75) return 'warning';
    return 'good';
  };

  const handleRecalculate = async (orgId: string, orgName: string) => {
    try {
      setRecalculating(orgId);
      setSuccessMessage(null);
      setError(null);

      const result = await recalculateSeatUsage(orgId);

      setSuccessMessage(`${orgName}: Recalculated seats - ${result.analyst} analysts, ${result.bank} bank users`);

      // Refresh data
      await fetchOrganizations();
    } catch (err) {
      console.error('Error recalculating seats:', err);
      setError(`Failed to recalculate seats for ${orgName}`);
    } finally {
      setRecalculating(null);
    }
  };

  if (loading) {
    return (
      <div className="license-page">
        <h1>License Overview</h1>
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="license-page">
        <h1>License Overview</h1>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="license-page">
      <h1>License Overview</h1>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {/* Summary Cards */}
      <div className="summary-grid">
        <div className="summary-card">
          <h3>Analyst Seats</h3>
          <div className="usage-display">
            <span className="usage-number">{summary.usedAnalystSeats}</span>
            <span className="usage-separator">/</span>
            <span className="usage-total">{summary.totalAnalystSeats}</span>
          </div>
          <div className="progress-bar">
            <div
              className={`progress-fill ${getUsageClass(getUsagePercentage(summary.usedAnalystSeats, summary.totalAnalystSeats))}`}
              style={{ width: `${getUsagePercentage(summary.usedAnalystSeats, summary.totalAnalystSeats)}%` }}
            />
          </div>
          <p className="usage-label">
            {getUsagePercentage(summary.usedAnalystSeats, summary.totalAnalystSeats)}% used
          </p>
        </div>

        {effectiveIsPlatformAdmin && (
          <div className="summary-card">
            <h3>Bank Seats</h3>
            <div className="usage-display">
              <span className="usage-number">{summary.usedBankSeats}</span>
              <span className="usage-separator">/</span>
              <span className="usage-total">{summary.totalBankSeats}</span>
            </div>
            <div className="progress-bar">
              <div
                className={`progress-fill ${getUsageClass(getUsagePercentage(summary.usedBankSeats, summary.totalBankSeats))}`}
                style={{ width: `${getUsagePercentage(summary.usedBankSeats, summary.totalBankSeats)}%` }}
              />
            </div>
            <p className="usage-label">
              {getUsagePercentage(summary.usedBankSeats, summary.totalBankSeats)}% used
            </p>
          </div>
        )}
      </div>

      {/* Organization Breakdown */}
      <div className="org-breakdown">
        <h2>Organization Breakdown</h2>
        <div className="breakdown-table">
          <table>
            <thead>
              <tr>
                <th>Organization</th>
                <th>Type</th>
                <th>Analyst Seats</th>
                <th>Usage</th>
                {effectiveIsPlatformAdmin && <th>Bank Seats</th>}
                {effectiveIsPlatformAdmin && <th>Usage</th>}
                {effectiveIsPlatformAdmin && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {organizations.map(org => {
                const analystPct = getUsagePercentage(
                  org.licenses?.analyst_seats_used || 0,
                  org.licenses?.analyst_seats || 0
                );
                const bankPct = getUsagePercentage(
                  org.licenses?.bank_seats_used || 0,
                  org.licenses?.bank_seats || 0
                );

                return (
                  <tr key={org.id}>
                    <td className="org-name">{org.name}</td>
                    <td>
                      <span className={`type-badge ${org.type}`}>
                        {org.type.toUpperCase()}
                      </span>
                    </td>
                    <td>
                      {org.licenses?.analyst_seats_used || 0} / {org.licenses?.analyst_seats || 0}
                    </td>
                    <td>
                      <div className="mini-progress">
                        <div
                          className={`mini-fill ${getUsageClass(analystPct)}`}
                          style={{ width: `${analystPct}%` }}
                        />
                      </div>
                      <span className="mini-label">{analystPct}%</span>
                    </td>
                    {effectiveIsPlatformAdmin && (
                      <>
                        <td>
                          {org.licenses?.bank_seats_used || 0} / {org.licenses?.bank_seats || 0}
                        </td>
                        <td>
                          <div className="mini-progress">
                            <div
                              className={`mini-fill ${getUsageClass(bankPct)}`}
                              style={{ width: `${bankPct}%` }}
                            />
                          </div>
                          <span className="mini-label">{bankPct}%</span>
                        </td>
                      </>
                    )}
                    {/* Only actual platform admins can recalculate (requires write access to org) */}
                    {isPlatformAdmin && effectiveIsPlatformAdmin && (
                      <td>
                        <button
                          className="recalculate-btn"
                          onClick={() => handleRecalculate(org.id, org.name)}
                          disabled={recalculating === org.id}
                        >
                          {recalculating === org.id ? 'Syncing...' : 'Recalculate'}
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>

          {organizations.length === 0 && (
            <div className="no-data">
              No organizations found.
            </div>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="legend">
        <h3>Usage Legend</h3>
        <div className="legend-items">
          <div className="legend-item">
            <span className="legend-color good"></span>
            <span>Good (&lt; 75%)</span>
          </div>
          <div className="legend-item">
            <span className="legend-color warning"></span>
            <span>Warning (75-90%)</span>
          </div>
          <div className="legend-item">
            <span className="legend-color critical"></span>
            <span>Critical (&gt; 90%)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LicenseOverview;
